/**
 * Derive a 12-month YearCalendar cell array from seeding windows. Months
 * inside the primary optimal range get the primary fill; months inside
 * the primary acceptable range or any secondary window get the secondary
 * fill. Mirrors the visual logic of the existing SeedingTimeline, just
 * binned to whole months so it can sit above the timeline as a glance card.
 */

import type { YearCalendarCell } from "./year-calendar-types"
import type { SeedingWindow } from "./seeding-calculator"

const S_PRIMARY = "#16a34a" // green-600
const S_SECONDARY = "#dcfce7" // green-100
const S_OFF = "#f9fafb" // gray-50

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

export interface SeedingCalendar {
  cells: YearCalendarCell[]
  bestMonth?: number
}

type WindowInput = Omit<SeedingWindow, "optimalStart" | "optimalEnd" | "acceptableStart" | "acceptableEnd" | "tooLateAfter"> & {
  optimalStart: Date | string
  optimalEnd: Date | string
  acceptableStart: Date | string
  acceptableEnd: Date | string
}

function toDate(d: Date | string): Date {
  return typeof d === "string" ? new Date(d) : d
}

/** Returns the set of month indices (0-11) intersected by [start, end]. */
function monthsInRange(start: Date, end: Date): Set<number> {
  const set = new Set<number>()
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return set
  if (end.getTime() < start.getTime()) return set
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1)
  const last = new Date(end.getFullYear(), end.getMonth(), 1)
  let safety = 0
  while (cursor.getTime() <= last.getTime() && safety < 24) {
    set.add(cursor.getMonth())
    cursor.setMonth(cursor.getMonth() + 1)
    safety++
  }
  return set
}

export function buildSeedingCalendar(
  primaryWindow: WindowInput | null | undefined,
  secondaryWindow: WindowInput | null | undefined,
): SeedingCalendar | null {
  if (!primaryWindow) return null

  const primaryOptimal = monthsInRange(
    toDate(primaryWindow.optimalStart),
    toDate(primaryWindow.optimalEnd),
  )
  const primaryAcceptable = monthsInRange(
    toDate(primaryWindow.acceptableStart),
    toDate(primaryWindow.acceptableEnd),
  )
  const secondaryOptimal = secondaryWindow
    ? monthsInRange(toDate(secondaryWindow.optimalStart), toDate(secondaryWindow.optimalEnd))
    : new Set<number>()
  const secondaryAcceptable = secondaryWindow
    ? monthsInRange(
        toDate(secondaryWindow.acceptableStart),
        toDate(secondaryWindow.acceptableEnd),
      )
    : new Set<number>()

  const cells: YearCalendarCell[] = MONTH_LABELS.map((label, i) => {
    if (primaryOptimal.has(i)) return { i, label, fill: S_PRIMARY }
    if (
      primaryAcceptable.has(i) ||
      secondaryOptimal.has(i) ||
      secondaryAcceptable.has(i)
    ) {
      return { i, label, fill: S_SECONDARY }
    }
    return { i, label, fill: S_OFF }
  })

  const bestMonth = toDate(primaryWindow.optimalStart).getMonth()

  return { cells, bestMonth }
}
